import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Flex, Heading, Text, Button, HStack, VStack, Badge, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, useToast, IconButton, Code, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, useDisclosure, Switch, FormLabel,
} from "@chakra-ui/react";
import { CopyIcon, RepeatIcon } from "@chakra-ui/icons";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";

function timeAgo(d) {
  if (!d) return "";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

export default function PasswordResetsPage() {
  useShellHeader({ title: "Password Resets", breadcrumb: "People & Org · Access" }, []);
  const toast = useToast();
  const disc = useDisclosure();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [reveal, setReveal] = useState(null); // { email, username, tempPassword }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api("/admin/password-resets");
      setRequests(data.requests || []);
    } catch (e) {
      toast({ title: e.message || "Could not load requests", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const pending = requests.filter((r) => r.status === "pending");
  const resolved = requests.filter((r) => r.status === "resolved");
  const visible = showResolved ? requests : pending;

  async function doReset(r) {
    setBusyId(r._id);
    try {
      const data = await api(`/admin/password-resets/${r._id}/reset`, { method: "POST" });
      setReveal({
        email: data.email || (r.user && r.user.email) || r.email,
        username: data.username || (r.user && r.user.username) || r.username,
        tempPassword: data.tempPassword,
      });
      disc.onOpen();
      await load();
    } catch (e) {
      toast({ title: e.message || "Reset failed", status: "error" });
    } finally {
      setBusyId(null);
    }
  }

  async function doResolve(r) {
    setBusyId(r._id);
    try {
      await api(`/admin/password-resets/${r._id}/resolve`, { method: "POST" });
      toast({ title: "Marked resolved", status: "success" });
      await load();
    } catch (e) {
      toast({ title: e.message || "Could not update", status: "error" });
    } finally {
      setBusyId(null);
    }
  }

  function copyPw() {
    if (reveal && reveal.tempPassword && navigator.clipboard) {
      navigator.clipboard.writeText(reveal.tempPassword)
        .then(() => toast({ title: "Copied", status: "success", duration: 1200 }))
        .catch(() => {});
    }
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
        <Box>
          <Heading size="md">Password Reset Requests</Heading>
          <Text fontSize="sm" color="gray.500">
            Staff who used “Forgot password?” on the login screen appear here. Reset to a
            temporary password in one click, then share it with them.
          </Text>
        </Box>
        <HStack>
          <FormLabel htmlFor="show-resolved" m={0} fontSize="sm" color="gray.500">Show resolved</FormLabel>
          <Switch id="show-resolved" isChecked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
          <Tooltip label="Refresh">
            <IconButton aria-label="Refresh" icon={<RepeatIcon />} size="sm" variant="ghost" onClick={load} isLoading={loading} />
          </Tooltip>
        </HStack>
      </Flex>

      <HStack mb={4} spacing={3}>
        <Badge colorScheme="orange" px={2} py={1} rounded="md">{pending.length} pending</Badge>
        <Badge colorScheme="gray" px={2} py={1} rounded="md">{resolved.length} resolved</Badge>
      </HStack>

      {loading ? (
        <Flex justify="center" py={10}><Spinner /></Flex>
      ) : visible.length === 0 ? (
        <Box py={10} textAlign="center" color="gray.500">
          {showResolved ? "No requests yet." : "No pending password reset requests. ✨"}
        </Box>
      ) : (
        <Box overflowX="auto" borderWidth="1px" rounded="lg">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Staff</Th>
                <Th>Email</Th>
                <Th>Requested</Th>
                <Th>Status</Th>
                <Th textAlign="right">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {visible.map((r) => {
                const name = (r.user && r.user.username) || r.username || "—";
                const email = (r.user && r.user.email) || r.email;
                const userMissing = !r.user;
                return (
                  <Tr key={r._id}>
                    <Td fontWeight={600}>{name}</Td>
                    <Td>{email}</Td>
                    <Td>{timeAgo(r.updatedAt)}</Td>
                    <Td>
                      {r.status === "pending" ? (
                        <Badge colorScheme="orange">pending</Badge>
                      ) : (
                        <Badge colorScheme="green">resolved</Badge>
                      )}
                    </Td>
                    <Td textAlign="right">
                      {r.status === "pending" ? (
                        <HStack justify="flex-end" spacing={2}>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            isLoading={busyId === r._id}
                            isDisabled={userMissing}
                            onClick={() => doReset(r)}
                          >
                            Reset password
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            isLoading={busyId === r._id}
                            onClick={() => doResolve(r)}
                          >
                            Mark done
                          </Button>
                        </HStack>
                      ) : (
                        <Text fontSize="xs" color="gray.400">
                          {r.resolvedBy ? `by ${r.resolvedBy.username || "admin"}` : "—"}
                        </Text>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Temp password reveal */}
      <Modal isOpen={disc.isOpen} onClose={disc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Temporary password set</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Share this with <b>{reveal && reveal.username}</b> ({reveal && reveal.email}).
                It works right away. Ask them to change it after logging in.
              </Text>
              <HStack>
                <Code fontSize="lg" px={3} py={2} rounded="md" flex="1">{reveal && reveal.tempPassword}</Code>
                <Tooltip label="Copy">
                  <IconButton aria-label="Copy password" icon={<CopyIcon />} onClick={copyPw} />
                </Tooltip>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={disc.onClose}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
